import React, { useMemo, useCallback, useState, useEffect } from "react";
import { observer } from "mobx-react-lite";
import { useStore } from "../../helpers";
import { useParams } from "react-router-dom";
import { AgGridReact } from "@ag-grid-community/react";
import { ModuleRegistry } from "@ag-grid-community/core";
import { ClientSideRowModelModule } from "@ag-grid-community/client-side-row-model";
import type { ColDef, GetRowIdFunc, GetRowIdParams } from "@ag-grid-community/core";
import "@ag-grid-community/styles/ag-grid.css";
import "@ag-grid-community/styles/ag-theme-alpine.css";
import { toast } from "react-toastify";
import { WebLinkModal } from "./components/WebLinkModal";
import { DeleteWebLinkModal } from "./components/DeleteWebLinkModal";
import { WebLinkStatusBadge } from "./components/WebLinkStatusBadge";
import { getLinkStatus, formatDate } from "./utils/weblink-utils";
import { WebLink } from "../../stores/active-survey-store/weblink-types";
import { useWebLinksList } from "./hooks/useWebLinkQueries";
import PageShell from "../../layouts/PageShell";
import { FTPBreadCrumbs } from "../../components-new";
import {
  Box,
  Button,
  Typography,
  IconButton,
  CircularProgress,
  Alert,
  Pagination,
  Stack,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

ModuleRegistry.registerModules([ClientSideRowModelModule]);

export const SurveyWebLinkManagement = observer(() => {
  const { activeSurveyStore, userStore } = useStore();
  const { projectid } = useParams<{ projectid: string }>();
  const [paginationPageSize, setPaginationPageSize] = useState<number>(25);
  const [currentPage, setCurrentPage] = useState<number>(0);

  const userProfile = userStore.user;
  const role = userProfile?.["role"];
  const isRootOrClient = role === "root" || role === "client";

  // Calculate offset from current page and page size
  const offset = currentPage * paginationPageSize;
  const count = paginationPageSize;

  // Fetch weblinks using TanStack Query with pagination
  const { data: weblinksData, isLoading: weblinksLoading } = useWebLinksList(
    isRootOrClient ? projectid : undefined,
    offset,
    count
  );


  // Update store when data changes (for compatibility with existing code)
  useEffect(() => {
    if (weblinksData) {
      activeSurveyStore.weblinks = weblinksData.weblinks;
      activeSurveyStore.allWeblinks = weblinksData.weblinks;
      activeSurveyStore.weblinksTotal = weblinksData.total;
      activeSurveyStore.weblinksOffset = weblinksData.offset;
      activeSurveyStore.weblinksLimit = weblinksData.limit;
    }
  }, [weblinksData, activeSurveyStore]);


  // Check authorization
  useEffect(() => {
    if (!isRootOrClient) {
      toast.error("You don't have permission to access this page");
    }
  }, [isRootOrClient]);

  const handleCreateClick = () => {
    activeSurveyStore.setCreateWebLinkModalOpen(true);
  };

  const handleEditClick = (weblink: WebLink) => {
    activeSurveyStore.setActiveWebLink(weblink);
    activeSurveyStore.setEditWebLinkModalOpen(true);
  };

  const handleDeleteClick = (weblink: WebLink) => {
    activeSurveyStore.setActiveWebLink(weblink);
    activeSurveyStore.setDeleteWebLinkModalOpen(true);
  };

  const handleCopyUrl = (guid: string) => {
    const url = activeSurveyStore.generateWebLinkPublicUrl(guid);
    navigator.clipboard.writeText(url);
    toast.success("URL copied to clipboard");
  };

  const getRowId = useCallback<GetRowIdFunc>(
    ({ data }: GetRowIdParams) => data?.id?.toString() || "",
    []
  );

  const columnDefs = useMemo<ColDef[]>(
    () => [
      {
        headerName: "Title",
        field: "title",
        flex: 1,
        minWidth: 150,
        cellRenderer: (params: any) => {
          const title = params.value;
          return (
            <span className={title ? "text-indigo-600 font-medium" : "text-gray-400"}>
              {title || "-"}
            </span>
          );
        },
        sortable: true,
        filter: false,
      },
      {
        headerName: "Survey Name",
        field: "survey_name",
        flex: 1,
        minWidth: 150,
        cellClass: "text-gray-700",
        valueGetter: (params: any) => params.data?.survey_name || "-",
        sortable: true,
        filter: false,
      },
      // {
      //   headerName: "GUID",
      //   field: "guid",
      //   flex: 1,
      //   minWidth: 200,
      //   cellRenderer: (params: any) => {
      //     const guid = params.value;
      //     return (
      //       <div className="flex items-center gap-2">
      //         <span className="font-mono text-sm">{guid}</span>
      //         <button
      //           onClick={(e) => {
      //             e.stopPropagation();
      //             handleCopyGuid(guid);
      //           }}
      //           className="text-blue-500 hover:text-blue-700"
      //           title="Copy GUID"
      //         >
      //           <i className="fa-solid fa-copy text-xs"></i>
      //         </button>
      //       </div>
      //     );
      //   },
      //   sortable: true,
      //   filter: true,
      // },
      {
        headerName: "Responses",
        field: "responses",
        width: 120,
        cellClass: "text-indigo-600 font-semibold text-center",
        cellRenderer: (params: any) => {
          const weblink = params.data as WebLink;
          if (!weblink) return null;
          return `${weblink.srids_count} / ${weblink.maxlimit}`;
        },
        sortable: true,
        filter: false,
      },
      {
        headerName: "Surveyor",
        field: "surveyor",
        flex: 1,
        minWidth: 120,
        cellRenderer: (params: any) => {
          const surveyor = params.value;
          return (
            <span className={surveyor ? "text-gray-700" : "text-gray-400"}>
              {surveyor?.name || surveyor?.id || "-"}
            </span>
          );
        },
        sortable: true,
        filter: false,
      },
      {
        headerName: "Created",
        field: "created_at",
        width: 140,
        cellClass: "text-gray-600 text-center",
        cellRenderer: (params: any) => formatDate(params.value),
        sortable: true,
        filter: false,
      },
      {
        headerName: "Active From",
        field: "active_from",
        width: 140,
        cellClass: "text-green-600 font-medium text-center",
        cellRenderer: (params: any) => formatDate(params.value),
        sortable: true,
        filter: false,
      },
      {
        headerName: "Active Until",
        field: "active_until",
        width: 140,
        cellClass: "text-red-600 font-medium text-center",
        cellRenderer: (params: any) => formatDate(params.value),
        sortable: true,
        filter: false,
      },
      {
        headerName: "Status",
        field: "status",
        width: 130,
        cellRenderer: (params: any) => {
          const weblink = params.data as WebLink;
          if (!weblink) return null;
          const status = getLinkStatus(weblink);
          return (
            <div className="flex justify-center pt-1">
              <WebLinkStatusBadge status={status} />
            </div>
          );
        },
        sortable: true,
        filter: false,
      },
      {
        headerName: "Actions",
        field: "actions",
        width: 140,
        pinned: "right",
        cellRenderer: (params: any) => {
          const weblink = params.data as WebLink;
          if (!weblink) return null;
          return (
            <div className="flex items-center justify-center gap-1 h-full">
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  handleCopyUrl(weblink.guid);
                }}
                size="small"
                title="Copy URL"
                sx={{ color: '#10b981', '&:hover': { color: '#059669', bgcolor: '#ecfdf5' } }}
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  handleEditClick(weblink);
                }}
                size="small"
                title="Edit"
                sx={{ color: '#3b82f6', '&:hover': { color: '#1d4ed8', bgcolor: '#eff6ff' } }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteClick(weblink);
                }}
                size="small"
                title="Delete"
                sx={{ color: '#ef4444', '&:hover': { color: '#dc2626', bgcolor: '#fef2f2' } }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </div>
          );
        },
        sortable: false,
        filter: false,
      },
    ],
    [handleEditClick, handleDeleteClick, handleCopyUrl]
  );

  const defaultColDef = useMemo(
    () => ({
      resizable: true,
      sortable: true,
      filter: false,
      cellStyle: { display: 'flex', alignItems: 'center' },
    }),
    []
  );

  const totalPages = useMemo(() => {
    if (!weblinksData?.total) return 0;
    return Math.ceil(weblinksData.total / paginationPageSize);
  }, [weblinksData?.total, paginationPageSize]);

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setCurrentPage(value - 1); // MUI Pagination is 1-based, our state is 0-based
  };

  const handlePageSizeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newPageSize = parseInt(event.target.value);
    setPaginationPageSize(newPageSize);
    setCurrentPage(0); // Reset to first page when page size changes
  };

  const title = (
    <Box>
      <Typography variant="h4" className="mb-2 font-bold text-gray-900">
        Survey Interview Links
      </Typography>
      <Typography variant="body1" className="text-gray-600">
        Manage survey interview links for this project
      </Typography>
    </Box>
  );

  const headerActions = (
    <Button
      onClick={handleCreateClick}
      variant="contained"
      startIcon={<AddIcon />}
      className="normal-case"
      sx={{ bgcolor: '#2563eb', '&:hover': { bgcolor: '#1d4ed8' } }}
    >
      Create Link
    </Button>
  );

  const crumbs = [
    { name: "DashBoard", href: `/project/${projectid}`, current: false },
    { name: "Survey Web Link", href: `/project/${projectid}/surveyweblink`, current: true },
  ];

  const breadcrumbs = (
    <div className="sticky top-[4rem] z-20 [&_nav]:mb-0 [&_nav]:!top-0 [&_nav]:!shadow-none [&_nav]:!border-b-0 [&_nav]:bg-transparent">
      <FTPBreadCrumbs crumbs={crumbs} />
    </div>
  );

  if (!isRootOrClient) {
    return (
      <PageShell
        title="Survey Interview Links"
        breadcrumbs={breadcrumbs}
        contentPadding="p-4"
      >
        <Box className="p-8">
          <Alert severity="error">
            You don't have permission to access this page.
          </Alert>
        </Box>
      </PageShell>
    );
  }

  return (
    <PageShell
      title={title}
      headerActions={headerActions}
      breadcrumbs={breadcrumbs}
      contentPadding="p-4"
    >
      <Box className="mb-4">
        {weblinksLoading ? (
          <Box className="flex items-center justify-center h-64">
            <Box className="flex flex-col items-center gap-3">
              <CircularProgress />
              <Typography className="text-gray-500">Loading weblinks...</Typography>
            </Box>
          </Box>
        ) : (
          <>
            {totalPages > 0 && (
              <Stack
                direction="row"
                spacing={2}
                alignItems="center"
                justifyContent="space-between"
                sx={{ my: 1, p: 1, bgcolor: "background.paper", borderRadius: 1 }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Typography variant="body2" className="text-gray-600">
                    Rows per page:
                  </Typography>
                  <select
                    value={paginationPageSize}
                    onChange={handlePageSizeChange}
                    style={{
                      padding: "4px 8px",
                      borderRadius: "4px",
                      border: "1px solid #ccc",
                      fontSize: "14px",
                    }}
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                    <option value={200}>200</option>
                  </select>
                  <Typography variant="body2" className="text-gray-600">
                    {weblinksData?.total ? (
                      <>
                        Showing {offset + 1} to {Math.min(offset + paginationPageSize, weblinksData.total)} of {weblinksData.total} links
                      </>
                    ) : null}
                  </Typography>
                </Box>
                <Pagination
                  count={totalPages}
                  page={currentPage + 1}
                  onChange={handlePageChange}
                  color="primary"
                  showFirstButton
                  showLastButton
                />
              </Stack>
            )}
            <div
              className="ag-theme-alpine rounded-xl shadow-lg border border-gray-200 overflow-hidden"
              style={{ height: "70vh", width: "100%" }}
            >
              <AgGridReact
                rowData={activeSurveyStore.allWeblinks}
                columnDefs={columnDefs}
                defaultColDef={defaultColDef}
                getRowId={getRowId}
                pagination={false}
                animateRows={true}
                suppressCellFocus={true}
                rowHeight={48}
                headerHeight={48}
                domLayout="normal"
              />
            </div>

          </>
        )}
      </Box>

      <WebLinkModal />
      <DeleteWebLinkModal />
    </PageShell>
  );
});

